import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Trash2, X, Sparkles, ShieldAlert, TrendingUp, TrendingDown, Layers, MapPin, Clock, Lock, ArrowUpRight } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { EmptyState } from "@/components/common/States";
import { IntelligenceCard } from "@/components/cards/Cards";
import { MarketBadge, RiskChip, TeamMono } from "@/components/badges/Badges";
import { useAnalysisList } from "@/features/analysis/AnalysisListContext";
import { analyzeCombination, CombinationInsight, AnalysisSelection } from "@/features/analysis/combinationAnalyzer";
import { resolveMarket } from "@/presentation/marketRegistry";
import { kickoffLabel, fmtPct } from "@/lib/format";
import { useI18n } from "@/i18n";

const RISK_TONE: Record<string, string> = {
  LOW: "text-atlas-400 border-atlas/30 bg-atlas/10",
  MEDIUM: "text-gold-400 border-gold/30 bg-gold/10",
  HIGH: "text-live-400 border-live/30 bg-live/10",
};

export default function AnalysisList() {
  const { d, lang } = useI18n();
  const { items, remove, clear, count } = useAnalysisList();
  const [insight, setInsight] = useState<CombinationInsight | null>(null);

  const run = () => setInsight(analyzeCombination(items, lang));

  return (
    <div data-testid="analysis-page">
      <PageHeader title={d.analysis.title} subtitle={d.analysis.subtitle}
        actions={count > 0 ? (
          <button onClick={() => { clear(); setInsight(null); }} data-testid="analysis-clear"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3.5 py-2 text-xs font-semibold text-muted-foreground transition hover:border-live/30 hover:text-live-400">
            <Trash2 className="h-3.5 w-3.5" /> {d.analysis.clear}
          </button>
        ) : undefined}
      />

      <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-gold/25 bg-gold/10 px-3 py-1 text-[11px] font-semibold text-gold-300">
        <ShieldAlert className="h-3.5 w-3.5" /> {d.analysis.notWager}
      </div>

      {count === 0 ? (
        <EmptyState title={d.analysis.empty} hint={d.analysis.emptyHint} icon={<ListChecks className="h-6 w-6" />} />
      ) : (
        <div className="grid gap-6 xl:grid-cols-[1.3fr_1fr]">
          {/* selections */}
          <div className="space-y-3" data-testid="analysis-selections">
            {items.map((s) => (
              <div key={s.fixtureId + s.marketCode} className="panel relative flex items-center gap-3 p-4" data-testid="analysis-item">
                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex -space-x-1"><TeamMono name={s.home} size={22} /><TeamMono name={s.away} size={22} /></div>
                    <p className="truncate text-sm font-semibold text-foreground">{s.home} <span className="text-muted-foreground">vs</span> {s.away}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <MarketBadge code={s.marketCode} backendLabel={s.marketName} size="sm" />
                    <RiskChip code={s.marketCode} />
                    {s.confidence != null && <span className="num text-[11px] text-muted-foreground">{d.common.confidence} <b className="text-foreground/90">{fmtPct(s.confidence)}</b></span>}
                    {s.kickoff && <span className="text-[11px] text-muted-foreground">{kickoffLabel(s.kickoff, lang)}</span>}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <Link to={`/app/match/${s.fixtureId}`} className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-muted-foreground hover:text-atlas-300"><ArrowUpRight className="h-4 w-4" /></Link>
                  <button onClick={() => { remove(s.fixtureId, s.marketCode); setInsight(null); }} data-testid={`analysis-remove-${s.fixtureId}`}
                    className="grid h-8 w-8 place-items-center rounded-lg border border-white/10 text-muted-foreground hover:border-live/30 hover:text-live-400"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
            <button onClick={run} data-testid="analysis-run"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-atlas-500 to-atlas-600 px-4 py-3 text-sm font-bold text-atlas-ink transition hover:shadow-glow">
              <Sparkles className="h-4 w-4" /> {d.analysis.analyze} · {count} {d.analysis.selections}
            </button>
          </div>

          {/* insight */}
          <div className="space-y-4">
            {!insight ? (
              <div className="panel flex flex-col items-center justify-center gap-2 px-6 py-12 text-center text-sm text-muted-foreground">
                <Sparkles className="h-6 w-6 text-atlas-400" />
                {lang === "tr" ? "Kombinasyonu incelemek için “Kuponu Analiz Et”e dokun." : "Tap “Analyze Combination” to review the slip."}
              </div>
            ) : (
              <>
                <IntelligenceCard title={<span className="inline-flex items-center gap-2"><ShieldAlert className="h-4 w-4 text-atlas-400" /> {d.analysis.overallRisk}</span>}>
                  <div className={`inline-flex items-center rounded-full border px-3 py-1 text-sm font-bold uppercase tracking-wider ${RISK_TONE[insight.overallRisk]}`}>{insight.overallRisk === "LOW" ? (lang === "tr" ? "Düşük" : "Low") : insight.overallRisk === "HIGH" ? (lang === "tr" ? "Yüksek" : "High") : (lang === "tr" ? "Orta" : "Medium")}</div>
                  <p className="mt-3 text-xs text-muted-foreground">{insight.overallRiskBasis}</p>
                </IntelligenceCard>

                <div className="grid gap-3 sm:grid-cols-2">
                  {insight.strongest && (
                    <div className="panel p-4"><p className="eyebrow text-atlas-300"><TrendingUp className="mr-1 inline h-3.5 w-3.5" />{d.analysis.strongest}</p><p className="mt-1.5 truncate text-sm font-semibold text-foreground">{insight.strongest.home} — {insight.strongest.away}</p><p className="mt-1 text-xs text-muted-foreground">{resolveMarket(insight.strongest.marketCode, lang, insight.strongest.marketName).label}{insight.strongest.confidence != null ? ` · ${fmtPct(insight.strongest.confidence)}` : ""}</p></div>
                  )}
                  {insight.weakest && (
                    <div className="panel p-4"><p className="eyebrow text-live-400"><TrendingDown className="mr-1 inline h-3.5 w-3.5" />{d.analysis.weakest}</p><p className="mt-1.5 truncate text-sm font-semibold text-foreground">{insight.weakest.home} — {insight.weakest.away}</p><p className="mt-1 text-xs text-muted-foreground">{resolveMarket(insight.weakest.marketCode, lang, insight.weakest.marketName).label}{insight.weakest.confidence != null ? ` · ${fmtPct(insight.weakest.confidence)}` : ""}</p></div>
                  )}
                </div>

                <IntelligenceCard title={<span className="inline-flex items-center gap-2"><Layers className="h-4 w-4 text-atlas-400" /> {lang === "tr" ? "Yapısal analiz" : "Structural analysis"}</span>}>
                  <div className="space-y-3 text-sm">
                    <Row icon={<Layers className="h-3.5 w-3.5" />} label={d.analysis.marketConc} value={insight.marketConcentration.slice(0, 3).map((m) => `${resolveMarket(m.key, lang).label} ×${m.count}`).join(", ")} />
                    <Row icon={<MapPin className="h-3.5 w-3.5" />} label={d.analysis.leagueConc} value={insight.leagueConcentration.slice(0, 3).map((m) => `${m.key} ×${m.count}`).join(", ")} />
                    {insight.highVarianceLegs.length > 0 && <Row icon={<ShieldAlert className="h-3.5 w-3.5 text-live-400" />} label={d.analysis.highVariance} value={insight.highVarianceLegs.map((s) => `${s.home}–${s.away}`).join(", ")} />}
                    {insight.timingConflicts.length > 0 && <Row icon={<Clock className="h-3.5 w-3.5 text-gold-400" />} label={d.analysis.timingConflict} value={insight.timingConflicts.map((t) => `${t.a} / ${t.b}`).join("; ")} />}
                    {insight.reviewCandidates.length > 0 && <Row icon={<TrendingDown className="h-3.5 w-3.5 text-gold-400" />} label={d.analysis.reviewCandidates} value={insight.reviewCandidates.map((s) => `${s.home}–${s.away}`).join(", ")} />}
                  </div>
                </IntelligenceCard>

                {/* ATLAS deep eval (locked until auth/backend) */}
                <div className="panel flex items-start gap-3 border-gold/20 bg-gold/[0.04] p-4" data-testid="analysis-atlas-deep">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-gold/10 text-gold-400"><Lock className="h-4 w-4" /></span>
                  <div><p className="text-sm font-semibold text-foreground">{d.analysis.deepTitle}</p><p className="mt-1 text-xs text-muted-foreground">{insight.atlasDeepEvaluation.reason}</p></div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line pb-2 last:border-0 last:pb-0">
      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">{icon}{label}</span>
      <span className="max-w-[60%] text-right text-xs font-medium text-foreground/90">{value}</span>
    </div>
  );
}
